import type {MyUser} from '@deezer/next/contexts/main/types';
import type {Claim} from '@deezer/next/services/api/Starmania/requests/claim/types';
import {
	getUserDefaultRoute,
	isArtistManager,
	isLabelGroup,
	isSomeClaimsPending,
	isSomeClaimsPendingOrApproved,
	isUserApproved,
} from '@deezer/next/shared/utils/user';
import {Routes} from '@constants/routes';
import type {NextRouter} from 'next/router';
import {FeatureLabel} from '@deezer/next/services/types/features';

export const extractQueryString = (
	param: string | string[] | undefined,
	defaultValue?: string,
) => {
	return (Array.isArray(param) ? param[0] : param) || defaultValue;
};

/**
 * Get the route to redirect to after login
 * @param user
 * @param claims
 */
export const getRouteOnLogin = (user: MyUser | undefined, claims: Claim[]) => {
	if (isUserApproved(user)) {
		return getUserDefaultRoute(user);
	} else {
		if (claims.length === 0) {
			return Routes.CLAIM;
		}
		return Routes.REQUEST_MONITORING;
	}
};
/**
 * Get the route to redirect to if the user tries to access certain parts
 * of the application without the proper state
 * @param user
 * @param right
 * @param userTypeRestriction
 * @param claims
 * @param isClaimsLoaded
 * @param router
 * @param activeArtist
 */
export const getForceRedirectedLoggedRoute = (
	user: MyUser | undefined,
	right: string | undefined,
	userTypeRestriction: number[] | undefined,
	claims: Claim[],
	isClaimsLoaded: boolean,
	router: NextRouter,
	activeArtist?: number,
): string | undefined => {
	const {pathname, query} = router;
	if (
		!isUserApproved(user) &&
		pathname !== Routes.CLAIM &&
		pathname !== Routes.REQUEST_MONITORING
	) {
		return Routes.REQUEST_MONITORING;
	}

	if (pathname === Routes.CLAIM || pathname === Routes.REQUEST_MONITORING) {
		if (isUserApproved(user)) {
			if (isArtistManager(user) || isLabelGroup(user)) {
				// Make sure artist manager and label cannot access claim screen if they have pending ones
				if (pathname === Routes.CLAIM && isSomeClaimsPending(claims)) {
					return Routes.REQUEST_MONITORING;
				}
			} else {
				// Ensure users that are not artist manager or label cannot access request monitoring or claim screens
				return getUserDefaultRoute(user, activeArtist);
			}
		} else {
			if (!isClaimsLoaded) return undefined;
			let replaceRoute;
			if (pathname === Routes.CLAIM) {
				if (isSomeClaimsPendingOrApproved(claims)) {
					replaceRoute = Routes.REQUEST_MONITORING;
				}
			}
			if (pathname === Routes.REQUEST_MONITORING) {
				if (claims.length === 0) {
					replaceRoute = Routes.CLAIM;
				}
			}
			if (replaceRoute && pathname !== replaceRoute) {
				return replaceRoute;
			}
		}
	} else {
		if (user) {
			if (right && !user.features?.find(f => f.label === right)?.enabled) {
				return getUserDefaultRoute(user, activeArtist);
			}
			if (
				userTypeRestriction &&
				!userTypeRestriction.includes(user.stakeholderType)
			) {
				return getUserDefaultRoute(user, activeArtist);
			}
			if (
				pathname.startsWith(Routes.USERS) &&
				query.id &&
				query.id !== String(user.id) &&
				!user.features.some(f => f.label === FeatureLabel.USERS_EDITOR)
			) {
				return getUserDefaultRoute(user, activeArtist);
			}
		}
	}

	return undefined;
};
